<template>
  <Tooltip position="left" :disabled="disableTooltip">
    <template #trigger>
      <button
        v-bind="$attrs"
        type="button"
        class="CopyClipboard"
        :class="{ 'CopyClipboard--iconOnly': iconOnly }"
        :aria-label="iconOnly ? t('ui.components.copyClipboard.copy') : undefined"
        @click="handleCopy"
      >
        <slot :isCopied :copiedText>
          <Icon v-if="iconOnly" icon="content-copy" size="sm" />
          <Tag v-else leftIcon="content-copy" :size>
            {{ isCopied ? copiedText : value }}
          </Tag>
        </slot>
      </button>
    </template>

    <p>{{ t('ui.components.copyClipboard.copy') }}</p>
  </Tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { refAutoReset, useClipboard } from '@vueuse/core';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Icon from '../../atoms/Icon/Icon.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import Tooltip from '../../atoms/Tooltip/Tooltip.vue';
import type { CopyClipboardEmits, CopyClipboardProps } from './CopyClipboard.model';

const props = defineProps<CopyClipboardProps>();
const emit = defineEmits<CopyClipboardEmits>();
defineOptions({ inheritAttrs: false });

const COPIED_LABEL_TIMEOUT = 2000;

const { t } = useI18n();
const { copy, isSupported } = useClipboard();
const isCopied = refAutoReset(false, COPIED_LABEL_TIMEOUT);
const copiedText = computed(() => t('ui.components.copyClipboard.copied'));

async function handleCopy() {
  if (!isSupported.value) {
    emit('error',  false, {
      isSupported: isSupported.value,
      error: new Error('Clipboard API not supported'),
    });
    return;
  }

  await performCopy();
}

async function performCopy() {
    try {
    await copy(props.value);
    isCopied.value = true;
    emit('copy', true);
  } catch (error) {
    emit('error', false, { isSupported: isSupported.value, error: error as Error });
  }
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';

@layer cvo-ui {
  .CopyClipboard {
    appearance: none;
    margin: 0;
    cursor: pointer;

    &--iconOnly {
      display: flex;
      align-items: center;
      justify-content: center;
      @include layout.square(vars.$dimensions-d-8);
      border: none;
      border-radius: vars.$dimensions-d-1;
      background-color: vars.$color-neutral-gray-100;
      color: vars.$color-neutral-gray-900;
    }
  }
}
</style>
